'use client';

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TournamentDetailsDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: () => Promise<void>;
  /** The page is re-reading the tournament: the notice waits for it and the confirm button too. */
  isCheckingPayments: boolean;
  /** Undefined when the API did not send it: the refund notice is left out rather than guessed. */
  isBelowMinimumTeams?: boolean;
  minTeams?: number;
  hasPaymentsWithoutGateway: boolean;
  /** The backend rejects cancelling a tournament with a fixture, so there are no refunds to describe. */
  hasFixture: boolean;
}

function RefundNotice({
  isCheckingPayments,
  isBelowMinimumTeams,
  minTeams,
  hasPaymentsWithoutGateway,
  hasFixture,
}: Omit<
  TournamentDetailsDeleteDialogProps,
  'isOpen' | 'onClose' | 'onDelete'
>) {
  if (isCheckingPayments) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Revisando los pagos del torneo...
      </p>
    );
  }
  if (hasFixture || isBelowMinimumTeams === undefined) return null;

  const cashReturn = hasPaymentsWithoutGateway
    ? ' La devolución del efectivo la coordinás vos con los jugadores.'
    : '';

  if (isBelowMinimumTeams) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          El torneo no llegó al mínimo de {minTeams} parejas completas, así que
          se reembolsan todos los pagos: los de Mercado Pago se devuelven a los
          jugadores y el fee de los pagos en efectivo sale de tu Cuenta
          Corriente.{cashReturn}
        </AlertDescription>
      </Alert>
    );
  }

  if (hasPaymentsWithoutGateway) {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          El torneo ya tiene el mínimo de {minTeams} parejas completas. Los
          pagos con Mercado Pago se devuelven a los jugadores, pero el fee de
          los pagos en efectivo o cargados a mano queda como deuda en tu Cuenta
          Corriente.{cashReturn}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert>
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        Los pagos con Mercado Pago se devuelven a los jugadores.
      </AlertDescription>
    </Alert>
  );
}

export function TournamentDetailsDeleteDialog({
  isOpen,
  onClose,
  onDelete,
  isCheckingPayments,
  isBelowMinimumTeams,
  minTeams,
  hasPaymentsWithoutGateway,
  hasFixture,
}: TournamentDetailsDeleteDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onDelete();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Cancelar torneo</DialogTitle>
          <DialogDescription>
            ¿Estás seguro de que deseas cancelar este torneo? Esta acción
            marcará el torneo como cancelado.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            El torneo será marcado como cancelado y no se eliminará del sistema.
          </AlertDescription>
        </Alert>

        <RefundNotice
          isCheckingPayments={isCheckingPayments}
          isBelowMinimumTeams={isBelowMinimumTeams}
          minTeams={minTeams}
          hasPaymentsWithoutGateway={hasPaymentsWithoutGateway}
          hasFixture={hasFixture}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={isSubmitting || isCheckingPayments}
          >
            {isSubmitting ? 'Procesando...' : 'Cancelar torneo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
